import { useEffect, useState } from "react";
import { adminClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { el as elLocale } from "date-fns/locale";
import {
  Loader2,
  Copy,
  Trash2,
  Plus,
  Link as LinkIcon,
  Calendar,
  Users,
  TrendingUp,
  UserPlus,
  UserCheck,
  UserX,
  Medal,
} from "lucide-react";
import { useT } from "@/lib/i18n";

const LEVEL_CLS = {
  golden: "bg-amber-100 text-amber-800 border border-amber-300",
  silver: "bg-slate-200 text-slate-700 border border-slate-300",
  open: "bg-emerald-100 text-emerald-800 border border-emerald-300",
};
const LEVEL_LABEL = { golden: "Golden", silver: "Silver", open: "Open" };
const LevelBadge = ({ level, testid }) =>
  level ? (
    <Badge data-testid={testid} className={`${LEVEL_CLS[level]} text-[10px] uppercase tracking-widest`}>
      {LEVEL_LABEL[level]}
    </Badge>
  ) : null;

export default function CoachTrainingTab() {
  return (
    <Tabs defaultValue="users" className="space-y-6">
      <TabsList className="bg-white border border-slate-100 rounded-2xl p-1 shadow-soft">
        <TabsTrigger
          value="users"
          data-testid="coach-tab-users"
          className="rounded-xl data-[state=active]:bg-ocean data-[state=active]:text-white"
        >
          <Users className="h-4 w-4 mr-1.5" /> <UsersLabel />
        </TabsTrigger>
        <TabsTrigger
          value="slots"
          data-testid="coach-tab-slots"
          className="rounded-xl data-[state=active]:bg-ocean data-[state=active]:text-white"
        >
          <Calendar className="h-4 w-4 mr-1.5" /> <SlotsLabel />
        </TabsTrigger>
        <TabsTrigger
          value="attendance"
          data-testid="coach-tab-attendance"
          className="rounded-xl data-[state=active]:bg-ocean data-[state=active]:text-white"
        >
          <TrendingUp className="h-4 w-4 mr-1.5" /> <AttendanceLabel />
        </TabsTrigger>
        <TabsTrigger
          value="invites"
          data-testid="coach-tab-invites"
          className="rounded-xl data-[state=active]:bg-ocean data-[state=active]:text-white"
        >
          <LinkIcon className="h-4 w-4 mr-1.5" /> <InvitesLabel />
        </TabsTrigger>
      </TabsList>

      <TabsContent value="users">
        <UsersPanel />
      </TabsContent>
      <TabsContent value="slots">
        <SlotsPanel />
      </TabsContent>
      <TabsContent value="attendance">
        <AttendancePanel />
      </TabsContent>
      <TabsContent value="invites">
        <InvitesPanel />
      </TabsContent>
    </Tabs>
  );
}

const UsersLabel = () => {
  const { t } = useT();
  return <span>{t("coach_tab_users")}</span>;
};
const SlotsLabel = () => {
  const { t } = useT();
  return <span>{t("coach_tab_slots")}</span>;
};
const AttendanceLabel = () => {
  const { t } = useT();
  return <span>{t("coach_tab_attendance")}</span>;
};
const InvitesLabel = () => {
  const { t } = useT();
  return <span>{t("coach_tab_invites")}</span>;
};

// ============ Users panel ============
function UsersPanel() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await adminClient.get("/training/athletes");
      setItems(data);
    } catch {
      toast.error("Failed to load athletes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm(t("coach_confirm_delete_athlete"))) return;
    try {
      await adminClient.delete(`/training/athletes/${id}`);
      toast.success(t("coach_athlete_deleted"));
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleLevel = async (a) => {
    const next = a.level === "silver" ? "golden" : "silver";
    try {
      await adminClient.patch(`/training/athletes/${a.id}`, { level: next });
      toast.success(`${a.name} ${a.surname} → ${next === "golden" ? "Golden" : "Silver"}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleActive = async (a) => {
    const nextActive = a.active === false;
    try {
      await adminClient.patch(`/training/athletes/${a.id}`, {
        active: nextActive,
      });
      toast.success(nextActive ? t("coach_athlete_enabled") : t("coach_athlete_disabled"));
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-soft border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100">
        <h2 className="font-heading font-bold text-xl text-slate-900">
          {t("coach_users_title")}
        </h2>
      </div>
      {loading ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ocean" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-slate-500">{t("coach_users_empty")}</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Level</TableHead>
                <TableHead className="hidden md:table-cell">Email</TableHead>
                <TableHead className="hidden md:table-cell">Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => {
                const isActive = a.active !== false;
                return (
                  <TableRow
                    key={a.id}
                    data-testid={`athlete-row-${a.id}`}
                    className={isActive ? "" : "opacity-60"}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{a.name} {a.surname}</span>
                        <LevelBadge level={a.level} testid={`athlete-level-badge-${a.id}`} />
                        {!isActive && (
                          <Badge className="bg-slate-200 text-slate-700 text-[10px] uppercase tracking-widest">
                            {t("coach_athlete_inactive")}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{a.username}</TableCell>
                    <TableCell data-testid={`athlete-level-${a.id}`}>
                      {a.level ? <LevelBadge level={a.level} /> : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600">
                      {a.email}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-500 text-xs">
                      {format(parseISO(a.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleLevel(a)}
                          data-testid={`toggle-level-${a.id}`}
                          title={a.level === "silver" ? "→ Golden" : "→ Silver"}
                          className={`rounded-full text-xs font-bold ${a.level === "silver" ? "text-amber-700 hover:bg-amber-50" : "text-slate-600 hover:bg-slate-100"}`}
                        >
                          <Medal className="h-4 w-4 mr-1" />
                          {a.level === "silver" ? "Golden" : "Silver"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleActive(a)}
                          data-testid={`toggle-athlete-${a.id}`}
                          title={isActive ? t("coach_disable") : t("coach_enable")}
                          className={
                            isActive
                              ? "text-slate-600 hover:bg-slate-100 rounded-full"
                              : "text-emerald-600 hover:bg-emerald-50 rounded-full"
                          }
                        >
                          {isActive ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove(a.id)}
                          data-testid={`del-athlete-${a.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ============ Slots panel ============
function SlotsPanel() {
  const { t, lang } = useT();
  const locale = lang === "el" ? elLocale : undefined;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [viewSlot, setViewSlot] = useState(null);
  const [allAthletes, setAllAthletes] = useState([]);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminClient.get("/training/slots/all");
      setItems(data);
    } catch {
      toast.error("Failed to load slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    adminClient
      .get("/training/athletes")
      .then((r) => setAllAthletes(r.data))
      .catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await adminClient.post("/training/slots/generate");
      toast.success(`Created ${data.created} slots`);
      load();
    } catch {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const cancelSlot = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Cancel this slot and all its bookings?")) return;
    try {
      await adminClient.delete(`/training/slots/${id}`);
      toast.success("Slot cancelled");
      load();
    } catch {
      toast.error("Cancel failed");
    }
  };

  const removeBooking = async (bookingId) => {
    if (!window.confirm("Remove this booking?")) return;
    try {
      await adminClient.delete(`/training/coach/bookings/${bookingId}`);
      toast.success("Booking removed");
      const { data } = await adminClient.get("/training/slots/all");
      setItems(data);
      const updated = data.find((s) => s.id === viewSlot?.id);
      if (updated) setViewSlot(updated);
    } catch {
      toast.error("Remove failed");
    }
  };

  const addAthleteToSlot = async (userId) => {
    if (!viewSlot) return;
    setAddingId(userId);
    try {
      await adminClient.post(
        `/training/coach/slots/${viewSlot.id}/add-athlete`,
        { user_id: userId }
      );
      toast.success(t("coach_athlete_added"));
      const { data } = await adminClient.get("/training/slots/all");
      setItems(data);
      const updated = data.find((s) => s.id === viewSlot.id);
      if (updated) setViewSlot(updated);
      setShowAddPicker(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setAddingId(null);
    }
  };

  const removeWaitlist = async (entryId) => {
    if (!window.confirm("Remove from waitlist?")) return;
    try {
      await adminClient.delete(`/training/coach/waitlists/${entryId}`);
      toast.success("Removed from waitlist");
      const { data } = await adminClient.get("/training/slots/all");
      setItems(data);
      const updated = data.find((s) => s.id === viewSlot?.id);
      if (updated) setViewSlot(updated);
    } catch {
      toast.error("Failed");
    }
  };

  const upcoming = items.filter(
    (s) => !s.cancelled && s.date >= new Date().toISOString().slice(0, 10)
  );

  return (
    <div className="bg-white rounded-3xl shadow-soft border border-slate-100">
      <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="font-heading font-bold text-xl text-slate-900">
          {t("coach_slots_title")}
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <AddDayControl onAdded={load} />
          <Button
            onClick={generate}
            disabled={generating}
            data-testid="generate-slots-btn"
            variant="outline"
            className="rounded-full"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            {t("coach_slots_generate")}
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ocean" />
        </div>
      ) : upcoming.length === 0 ? (
        <div className="p-10 text-center text-slate-500">{t("coach_slots_empty")}</div>
      ) : (
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {upcoming.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setViewSlot(s)}
              data-testid={`coach-slot-${s.id}`}
              className="text-left rounded-2xl border border-slate-100 p-4 hover:border-ocean hover:shadow-soft transition-all"
            >
              <div className="text-xs uppercase tracking-widest text-slate-500">
                {format(parseISO(s.date), "EEE, MMM d", { locale })}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-mono font-bold text-lg text-ocean">
                  {s.start_time} → {s.end_time}
                </div>
                <LevelBadge level={s.level} testid={`coach-slot-level-${s.id}`} />
              </div>
              <div className="flex items-center justify-between mt-2">
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    s.booked_count >= s.capacity
                      ? "bg-red-50 text-red-700 border-red-200"
                      : s.booked_count > 0
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : ""
                  }`}
                >
                  {s.booked_count}/{s.capacity}
                </Badge>
                <div className="flex items-center gap-1">
                  {s.waitlist_count > 0 && (
                    <Badge className="bg-sunset/15 text-sunset border-sunset/30 text-xs">
                      WL {s.waitlist_count}
                    </Badge>
                  )}
                  <span
                    onClick={(e) => cancelSlot(s.id, e)}
                    className="p-1.5 rounded-full text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
              {s.athletes && s.athletes.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
                  {s.athletes.slice(0, 3).map((a) => (
                    <li
                      key={a.user_id}
                      className="text-xs text-slate-700 truncate"
                    >
                      · {a.name} {a.surname}
                    </li>
                  ))}
                  {s.athletes.length > 3 && (
                    <li className="text-xs text-ocean font-semibold">
                      +{s.athletes.length - 3} more
                    </li>
                  )}
                </ul>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Slot detail dialog */}
      <Dialog open={!!viewSlot} onOpenChange={(o) => !o && setViewSlot(null)}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl">
              {viewSlot &&
                `${format(parseISO(viewSlot.date), "EEE, MMM d", {
                  locale,
                })}`}
            </DialogTitle>
            <DialogDescription>
              {viewSlot && (
                <>
                  <span className="font-mono font-bold text-ocean">
                    {viewSlot.start_time} → {viewSlot.end_time}
                  </span>{" "}
                  <LevelBadge level={viewSlot.level} />{" "}
                  · {viewSlot.booked_count}/{viewSlot.capacity} athletes
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto mt-2">
            {!viewSlot?.athletes?.length ? (
              <p className="text-center py-8 text-slate-500 text-sm">
                No athletes booked yet.
              </p>
            ) : (
              viewSlot.athletes.map((a, i) => (
                <div
                  key={a.booking_id}
                  data-testid={`view-athlete-${a.booking_id}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-sand/40 border border-sand-darker"
                >
                  <div className="h-9 w-9 rounded-full bg-ocean text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {a.name} {a.surname}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{a.email}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeBooking(a.booking_id)}
                    data-testid={`remove-booking-${a.booking_id}`}
                    className="text-red-600 hover:bg-red-50 rounded-full shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}

            {viewSlot &&
              viewSlot.booked_count < viewSlot.capacity &&
              (!showAddPicker ? (
                <Button
                  type="button"
                  onClick={() => setShowAddPicker(true)}
                  data-testid="open-add-athlete-picker"
                  variant="outline"
                  className="w-full h-11 rounded-2xl border-dashed border-ocean text-ocean hover:bg-ocean/5 mt-2"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("coach_add_athlete")}
                </Button>
              ) : (
                <div className="border border-ocean/30 rounded-2xl p-3 bg-ocean/5 mt-2 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-widest font-bold text-ocean">
                      {t("coach_pick_athlete")}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAddPicker(false)}
                      className="text-xs text-slate-500 hover:text-slate-900"
                      data-testid="close-add-athlete-picker"
                    >
                      ✕
                    </button>
                  </div>
                  {(() => {
                    const bookedIds = new Set(
                      (viewSlot.athletes || []).map((a) => a.user_id)
                    );
                    const available = allAthletes.filter(
                      (a) => !bookedIds.has(a.id) && a.active !== false
                    );
                    if (available.length === 0) {
                      return (
                        <p className="text-center text-xs text-slate-500 py-3">
                          {t("coach_no_more_athletes")}
                        </p>
                      );
                    }
                    return (
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {available.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            disabled={addingId === a.id}
                            onClick={() => addAthleteToSlot(a.id)}
                            data-testid={`add-athlete-${a.id}`}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-white hover:bg-ocean/10 text-left transition-colors disabled:opacity-50"
                          >
                            <span className="text-sm font-medium text-slate-900 truncate">
                              {a.name} {a.surname}
                            </span>
                            {addingId === a.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-ocean shrink-0" />
                            ) : (
                              <Plus className="h-4 w-4 text-ocean shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ))}

            {viewSlot?.waitlist?.length > 0 && (
              <>
                <div className="text-xs uppercase tracking-widest text-sunset font-bold mt-4 mb-2 px-1">
                  Waitlist ({viewSlot.waitlist.length})
                </div>
                {viewSlot.waitlist.map((w, i) => (
                  <div
                    key={w.id}
                    data-testid={`waitlist-entry-${w.id}`}
                    className="flex items-center gap-3 p-3 rounded-2xl bg-sunset/10 border border-sunset/30"
                  >
                    <div className="h-9 w-9 rounded-full bg-sunset text-white flex items-center justify-center font-bold text-sm shrink-0">
                      W{i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {w.name} {w.surname}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {w.email} · {w.status}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeWaitlist(w.id)}
                      data-testid={`remove-waitlist-${w.id}`}
                      className="text-red-600 hover:bg-red-50 rounded-full shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Attendance panel ============
function AttendancePanel() {
  const { t } = useT();
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/training/attendance", {
        params: { month: m },
      });
      setData(data);
    } catch {
      toast.error("Failed to load attendance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(month);
  }, [month]);

  return (
    <div className="bg-white rounded-3xl shadow-soft border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="font-heading font-bold text-xl text-slate-900">
          {t("coach_attendance_title")}
        </h2>
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="attendance-month-input"
          className="max-w-[200px] h-10 rounded-2xl"
        />
      </div>
      {loading ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ocean" />
        </div>
      ) : !data || data.athletes.length === 0 ? (
        <div className="p-10 text-center text-slate-500">
          {t("coach_attendance_empty")}
        </div>
      ) : (
        <>
          <div className="p-6 grid grid-cols-2 gap-3">
            <Stat label="Total sessions" value={data.total_sessions} />
            <Stat label="Athletes" value={data.athletes.length} />
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Athlete</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.athletes.map((a, i) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-slate-500">{i + 1}</TableCell>
                    <TableCell className="font-medium">
                      {a.name} {a.surname}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600">
                      {a.email}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className="bg-ocean text-white">{a.sessions}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-2xl bg-sand/40 border border-sand-darker p-4">
    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
      {label}
    </div>
    <div className="text-2xl font-heading font-black text-slate-900 mt-1">{value}</div>
  </div>
);

function AddDayControl({ onAdded }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [level, setLevel] = useState("");
  const [loading, setLoading] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [selected, setSelected] = useState([]);
  const todayIso = new Date().toISOString().slice(0, 10);

  const TIME_OPTIONS = [
    "07:00",
    "09:00", "10:30", "12:00", "13:30",
    "15:00", "16:30", "18:00", "19:30", "21:00",
  ];

  useEffect(() => {
    if (!open) return;
    adminClient
      .get("/training/athletes")
      .then((r) => setAthletes(r.data))
      .catch(() => toast.error("Failed to load athletes"));
  }, [open]);

  const toggle = (id) => {
    setSelected((list) =>
      list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
    );
  };

  const reset = () => {
    setDate("");
    setTime("18:00");
    setLevel("");
    setSelected([]);
  };

  const submit = async () => {
    if (!date) {
      toast.error("Pick a date");
      return;
    }
    if (!level) {
      toast.error("Pick a level (Silver / Golden / Open)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminClient.post("/training/slots/custom", {
        date,
        time,
        level,
        athlete_ids: selected,
      });
      const msg = `Slot ${data.time} → ${data.end_time}${
        data.prebooked > 0 ? ` · ${data.prebooked} pre-booked` : ""
      }`;
      if (data.created === 0 && data.prebooked === 0) {
        toast.info("Slot already exists");
      } else {
        toast.success(msg);
      }
      reset();
      setOpen(false);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const computeEnd = (t) => {
    const [h, m] = t.split(":").map(Number);
    const total = h * 60 + m + 90;
    const eh = Math.floor(total / 60);
    const em = total % 60;
    return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button
        onClick={() => setOpen(true)}
        data-testid="add-day-btn"
        className="rounded-full bg-sunset hover:bg-sunset-hover text-white"
      >
        <Plus className="h-4 w-4 mr-2" />
        {t("coach_add_day")}
      </Button>
      <DialogContent
        data-testid="add-day-dialog"
        className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl">
            {t("coach_add_day")}
          </DialogTitle>
          <DialogDescription>{t("coach_add_day_helper")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
                Date
              </Label>
              <Input
                type="date"
                value={date}
                min={todayIso}
                onChange={(e) => setDate(e.target.value)}
                data-testid="add-day-date-input"
                className="h-12 rounded-2xl"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
                Time
              </Label>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="add-day-time-select"
                className="w-full h-12 rounded-2xl border border-slate-200 px-3 bg-white font-mono text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-ocean/30 focus:border-ocean"
              >
                {TIME_OPTIONS.map((tm) => (
                  <option key={tm} value={tm}>
                    {tm} → {computeEnd(tm)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
              Level <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-2" data-testid="add-day-level-toggle">
              {["silver", "golden", "open"].map((lv) => (
                <button
                  key={lv}
                  type="button"
                  onClick={() => setLevel(lv)}
                  data-testid={`add-day-level-${lv}`}
                  className={`h-12 rounded-2xl border text-sm font-bold uppercase tracking-widest transition-colors ${
                    level === lv
                      ? lv === "golden"
                        ? "bg-amber-100 text-amber-800 border-amber-400"
                        : lv === "open"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-400"
                        : "bg-slate-200 text-slate-800 border-slate-400"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {LEVEL_LABEL[lv]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Pre-book athletes (optional)
              </div>
              <Badge variant="outline" className="text-xs">
                {selected.length}/8
              </Badge>
            </div>
            {athletes.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No athletes yet.
              </p>
            ) : (
              <div className="rounded-2xl border border-slate-200 p-3 max-h-64 overflow-y-auto space-y-1">
                {athletes.map((a) => {
                  const checked = selected.includes(a.id);
                  const disabled = !checked && selected.length >= 8;
                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer text-sm ${
                        checked
                          ? "bg-ocean/10 text-ocean font-semibold"
                          : disabled
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(a.id)}
                        data-testid={`prebook-${a.id}`}
                        className="h-4 w-4 accent-ocean"
                      />
                      <span className="truncate">
                        {a.name} {a.surname}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <Button
            onClick={submit}
            disabled={loading || !date || !level}
            data-testid="add-day-submit"
            className="w-full h-12 rounded-full bg-ocean hover:bg-ocean-hover text-white font-bold"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              `+ 1 slot${selected.length > 0 ? ` · ${selected.length} pre-booked` : ""}`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Invites panel ============
function InvitesPanel() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await adminClient.get("/training/invites");
      setItems(data);
    } catch {
      toast.error("Failed to load invites");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      await adminClient.post("/training/invites");
      toast.success("Invite link created");
      load();
    } catch {
      toast.error("Create failed");
    }
  };

  const remove = async (id) => {
    try {
      await adminClient.delete(`/training/invites/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copy = (token) => {
    const url = `${window.location.origin}/training?invite=${token}`;
    navigator.clipboard.writeText(url);
    toast.success(t("coach_invite_copied"));
  };

  return (
    <div className="bg-white rounded-3xl shadow-soft border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="font-heading font-bold text-xl text-slate-900">
          {t("coach_invites_title")}
        </h2>
        <Button
          onClick={create}
          data-testid="create-invite-btn"
          className="rounded-full bg-sunset hover:bg-sunset-hover text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("coach_invite_create")}
        </Button>
      </div>
      {loading ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ocean" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-slate-500">No invites yet.</div>
      ) : (
        <div className="p-6 space-y-3">
          {items.map((inv) => {
            const url = `${window.location.origin}/training?invite=${inv.token}`;
            const used = !!inv.used_by;
            return (
              <div
                key={inv.id}
                data-testid={`invite-row-${inv.id}`}
                className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                  used ? "bg-slate-50 border-slate-200" : "bg-sand/30 border-sand-darker"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {used ? (
                      <Badge className="bg-slate-200 text-slate-700">Used</Badge>
                    ) : (
                      <Badge className="bg-emerald-500 text-white">Active</Badge>
                    )}
                    <span className="text-xs text-slate-500">
                      {format(parseISO(inv.created_at), "MMM d, yyyy")}
                    </span>
                  </div>
                  <code className="text-xs text-slate-700 truncate block max-w-full">
                    {url}
                  </code>
                </div>
                <div className="flex gap-2">
                  {!used && (
                    <Button
                      size="sm"
                      onClick={() => copy(inv.token)}
                      data-testid={`copy-invite-${inv.id}`}
                      className="rounded-full bg-ocean hover:bg-ocean-hover text-white"
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      {t("coach_invite_copy")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(inv.id)}
                    className="text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
